import { getApiClient } from '../../axios-config';

/**
 * Project Assistant Schedule Tools API Service.
 *
 * Thin wrapper over the FieldTime-owned Schedule Tools API
 * (routes/ai/project-docs/assistant-route.js /
 * controllers/ai/project-docs/schedule-controller.js). Mirrors
 * assistant-api.js/documents-api.js's own conventions exactly -- the browser
 * never calls RAG directly, and never supplies a document id, version id,
 * content hash, or storage path: every generate call's body is intentionally
 * empty, since the authoritative source set is derived entirely server-side.
 *
 * Three schedule types exist today -- 'piping', 'ductwork', and
 * 'electrical_material' (see database_calls/ai/project-docs/
 * schedule-runs-db.js's own SCHEDULE_TYPES) -- each with its own named
 * public methods below (never a generic `generateSchedule(scheduleType,
 * ...)` surface: the URL path is schedule-type-specific server-side, so a
 * typo'd/invalid scheduleType string could otherwise silently 404 rather
 * than failing at the call site). Every method shares its actual HTTP-call
 * body via small private helpers (_generate/_list/_get/_download) purely to
 * avoid copy-pasting the same request shapes -- this is NOT a plugin
 * registry: adding a new tool means adding named public methods that call
 * the same private helpers, not registering anything.
 *
 * FINAL PRODUCT DIRECTION: Schedule Tools is a chat-only capability (see
 * ProjectDocumentsAssistantPage.js's own module docstring) -- the frontend
 * has no history/detail UI left, so `download*ScheduleRun` (bound to a
 * chat message's own toolResult.runId) is the only method a new schedule
 * type's frontend integration actually needs; `generate*Schedule`/
 * `list*ScheduleRuns`/`get*ScheduleRun` exist for Piping/Ductwork only as a
 * carryover from before that direction was locked in, not a pattern to
 * keep extending.
 */
class ScheduleAPI {
  /**
   * Generate a new Piping Schedule run for a project. Always creates a new
   * run (rerun uses this exact same call -- see schedule-controller.js's own
   * docstring: "rerun never resets an existing one").
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the resulting run (CURRENT or FAILED)
   */
  static async generatePipingSchedule(projectId, token) {
    return ScheduleAPI._generate('piping', projectId, token);
  }

  /**
   * List every saved Piping Schedule run for a project, most recently
   * requested first. Currentness/staleness is already reconciled server-side
   * before this resolves (see schedule-controller.js's listPiping).
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of run objects
   */
  static async listPipingScheduleRuns(projectId, token) {
    return ScheduleAPI._list('piping', projectId, token);
  }

  /**
   * Get one saved Piping Schedule run's full detail (including resultRows).
   * @param {string} projectId
   * @param {string} runId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the run
   */
  static async getPipingScheduleRun(projectId, runId, token) {
    return ScheduleAPI._get('piping', projectId, runId, token);
  }

  /**
   * Generate a new Ductwork Schedule run for a project. Structural sibling
   * of generatePipingSchedule above -- see that method's own docstring.
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the resulting run (CURRENT or FAILED)
   */
  static async generateDuctworkSchedule(projectId, token) {
    return ScheduleAPI._generate('ductwork', projectId, token);
  }

  /**
   * List every saved Ductwork Schedule run for a project. Structural
   * sibling of listPipingScheduleRuns above.
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of run objects
   */
  static async listDuctworkScheduleRuns(projectId, token) {
    return ScheduleAPI._list('ductwork', projectId, token);
  }

  /**
   * Get one saved Ductwork Schedule run's full detail. Structural sibling
   * of getPipingScheduleRun above.
   * @param {string} projectId
   * @param {string} runId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the run
   */
  static async getDuctworkScheduleRun(projectId, runId, token) {
    return ScheduleAPI._get('ductwork', projectId, runId, token);
  }

  /**
   * Download one saved Piping Schedule run as CSV.
   * @param {string} projectId
   * @param {string} runId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{blob: Blob, fileName: string}>}
   */
  static async downloadPipingScheduleRun(projectId, runId, token) {
    return ScheduleAPI._download('piping', projectId, runId, token);
  }

  /**
   * Download one saved Ductwork Schedule run as CSV. Structural sibling of
   * downloadPipingScheduleRun above.
   * @param {string} projectId
   * @param {string} runId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{blob: Blob, fileName: string}>}
   */
  static async downloadDuctworkScheduleRun(projectId, runId, token) {
    return ScheduleAPI._download('ductwork', projectId, runId, token);
  }

  /**
   * Download one saved Electrical Material Schedule run as CSV. Structural
   * sibling of downloadPipingScheduleRun above.
   * @param {string} projectId
   * @param {string} runId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{blob: Blob, fileName: string}>}
   */
  static async downloadElectricalMaterialScheduleRun(projectId, runId, token) {
    return ScheduleAPI._download(
      'electrical-material',
      projectId,
      runId,
      token
    );
  }

  // --- shared request bodies, private -- see class docstring for why these
  //     are NOT exposed as a generic public surface -----------------------

  static async _generate(scheduleType, projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/schedules/${scheduleType}/runs`
    );
    return response.data.data.run;
  }

  static async _list(scheduleType, projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/schedules/${scheduleType}/runs`
    );
    return response.data.data.runs || [];
  }

  static async _get(scheduleType, projectId, runId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/schedules/${scheduleType}/runs/${runId}`
    );
    return response.data.data.run;
  }

  /**
   * Server sets a normal Content-Disposition: attachment header (see
   * schedule-controller.js's sendScheduleExportCsv) -- this reads the file
   * name FROM that response header rather than reconstructing one
   * client-side, so the browser never guesses at naming/date-stamping.
   * Falls back to a generic name only if the header is somehow missing.
   */
  static _fileNameFromContentDisposition(contentDisposition, fallback) {
    if (typeof contentDisposition !== 'string') return fallback;
    const match = contentDisposition.match(/filename="?([^";]+)"?/);
    return match ? match[1] : fallback;
  }

  static async _download(scheduleType, projectId, runId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/schedules/${scheduleType}/runs/${runId}/export`,
      { responseType: 'blob' }
    );
    const fileName = ScheduleAPI._fileNameFromContentDisposition(
      response.headers['content-disposition'],
      `${scheduleType}-schedule.csv`
    );
    return { blob: response.data, fileName };
  }
}

export default ScheduleAPI;
